/**
 * AP-INV-01: My Invoices List
 * Route: /portal/invoices (portal-protected)
 *
 * Supplier's submitted and draft invoices. Status filters, AP reference,
 * amounts, PO linkage and navigation to detail/new invoice.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Plus, RefreshCw, ChevronRight,
  CheckCircle2, XCircle, Clock, AlertTriangle,
  Edit3, Archive, Send,
} from "lucide-react";
import { usePortalUser } from "@/components/PortalProtectedRoute";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  DRAFT:        { label: "Draft",        bg: "#F8F9FA", text: "#495057", border: "#DEE2E6", Icon: Edit3 },
  SUBMITTED:    { label: "Submitted",    bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", Icon: Send },
  UNDER_REVIEW: { label: "Under Review", bg: "#FEF3C7", text: "#92400E", border: "#FDE68A", Icon: Clock },
  APPROVED:     { label: "Approved",     bg: "#F0FDF4", text: "#166534", border: "#BBF7D0", Icon: CheckCircle2 },
  REJECTED:     { label: "Rejected",     bg: "#FEF2F2", text: "#991B1B", border: "#FECACA", Icon: XCircle },
  DISPUTED:     { label: "Disputed",     bg: "#FFF7ED", text: "#9A3412", border: "#FED7AA", Icon: AlertTriangle },
  VOIDED:       { label: "Voided",       bg: "#F5F5F5", text: "#9CA3AF", border: "#E5E7EB", Icon: Archive },
};

const FILTERS = [
  { label: "All", value: "" },
  { label: "Drafts", value: "DRAFT" },
  { label: "Submitted", value: "SUBMITTED" },
  { label: "Under Review", value: "UNDER_REVIEW" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
];

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency,
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(minor / 100);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD", Icon: FileText };
  const Ic = cfg.Icon;
  return (
    <span data-testid={`status-badge-${status}`} style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: 600,
      background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`, letterSpacing: "0.3px",
    }}>
      <Ic size={11} />
      {cfg.label}
    </span>
  );
}

export default function SupplierInvoices() {
  const { portalUser } = usePortalUser();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/api/portal/invoices`, { params, withCredentials: true });
      setInvoices(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="supplier-invoices-page" style={{ minHeight: "100vh", background: "#FBF7F2", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Nav */}
      <nav style={{ background: "#1A1A1A", borderBottom: "1px solid rgba(201,161,95,0.2)", padding: "0 24px", height: "60px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px", fontWeight: 600, color: "#C9A15F" }}>Prashasta</span>
          <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)" }} />
          <span style={{ fontSize: "12px", color: "#8A7A6A", letterSpacing: "1px", textTransform: "uppercase" }}>Partner Portal</span>
          <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)" }} />
          {[{ label: "Purchase Orders", path: "/portal/dashboard", testId: "portal-nav-pos" },
            { label: "My Deliveries",   path: "/portal/deliveries", testId: "portal-nav-deliveries" },
            { label: "My Invoices",     path: "/portal/invoices", testId: "portal-nav-invoices" }
          ].map(({ label, path, testId }) => (
            <button key={path} data-testid={testId} onClick={() => navigate(path)}
              style={{ fontSize: "13px", color: path === "/portal/invoices" ? "#F5EFE6" : "#C9A15F", background: "none", border: "none", cursor: "pointer", padding: "4px 8px", borderBottom: path === "/portal/invoices" ? "2px solid #C9A15F" : "2px solid transparent" }}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "12px", color: "#8A7A6A" }}>{portalUser?.name || portalUser?.email}</span>
        </div>
      </nav>

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 24px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(22px,4vw,30px)", fontWeight: 600, color: "#1A1A1A", margin: "0 0 4px" }}>
              My Invoices
            </h2>
            <p style={{ fontSize: "13px", color: "#6B5B4A", margin: 0 }}>
              {total} invoice{total !== 1 ? "s" : ""} — submit digital invoices against accepted purchase orders
            </p>
          </div>
          <button
            data-testid="new-invoice-btn"
            onClick={() => navigate("/portal/invoices/new")}
            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "#1A1A1A", color: "#C9A15F", border: "1px solid #C9A15F", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}
          >
            <Plus size={16} />
            New Invoice
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
          {FILTERS.map(({ label, value }) => (
            <button key={value || "all"} data-testid={`filter-${value || "all"}`}
              onClick={() => { setStatusFilter(value); setPage(1); }}
              style={{
                padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 500,
                cursor: "pointer", border: "1px solid", transition: "all 0.15s",
                background: statusFilter === value ? "#1A1A1A" : "transparent",
                color: statusFilter === value ? "#F5EFE6" : "#6B5B4A",
                borderColor: statusFilter === value ? "#1A1A1A" : "#DDD6CE",
              }}>
              {label}
            </button>
          ))}
          <button data-testid="refresh-invoices-btn" onClick={load} disabled={loading}
            style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "4px", padding: "6px 12px", background: "transparent", border: "1px solid #DDD6CE", borderRadius: "20px", cursor: "pointer", fontSize: "12px", color: "#6B5B4A" }}>
            <RefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
        </div>

        {/* Table */}
        <div style={{ background: "#FFF", border: "1px solid #EDE8E0", borderRadius: "12px", overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: "60px", textAlign: "center", color: "#9CA3AF", fontSize: "14px" }}>
              Loading invoices…
            </div>
          ) : invoices.length === 0 ? (
            <div data-testid="invoices-empty-state" style={{ padding: "60px", textAlign: "center" }}>
              <FileText size={40} style={{ color: "#DDD6CE", marginBottom: "16px" }} />
              <p style={{ fontSize: "15px", color: "#6B5B4A", margin: "0 0 8px" }}>No invoices yet</p>
              <p style={{ fontSize: "13px", color: "#9CA3AF", margin: "0 0 20px" }}>
                {statusFilter ? `No invoices with status "${statusFilter}"` : "Submit your first invoice against an accepted purchase order"}
              </p>
              {!statusFilter && (
                <button onClick={() => navigate("/portal/invoices/new")}
                  style={{ padding: "8px 18px", background: "#1A1A1A", color: "#C9A15F", border: "1px solid #C9A15F", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
                  New Invoice
                </button>
              )}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#F9F5F0" }}>
                    {["AP Reference", "Supplier Inv#", "PO Number", "Invoice Date", "Total", "Status", ""].map((h, i) => (
                      <th key={i} style={{ padding: "12px 16px", textAlign: i === 4 ? "right" : "left", fontSize: "11px", fontWeight: 700, color: "#6B5B4A", letterSpacing: "0.5px", textTransform: "uppercase", borderBottom: "1px solid #EDE8E0", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, idx) => (
                    <tr key={inv.id} data-testid={`invoice-row-${idx}`}
                      onClick={() => navigate(`/portal/invoices/${inv.id}`)}
                      style={{ cursor: "pointer", transition: "background 0.15s", borderBottom: idx < invoices.length - 1 ? "1px solid #F5F0EB" : "none" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#FAF7F2"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={{ padding: "14px 16px" }}>
                        <span style={{ fontFamily: "monospace", fontSize: "12px", fontWeight: 600, color: inv.ap_reference ? "#1A1A1A" : "#9CA3AF" }}>
                          {inv.ap_reference || "— draft —"}
                        </span>
                      </td>
                      <td style={{ padding: "14px 16px", fontSize: "13px", color: "#1A1A1A", fontWeight: 500 }}>
                        {inv.supplier_invoice_number || "—"}
                      </td>
                      <td style={{ padding: "14px 16px", fontSize: "12px", color: "#6B5B4A" }}>
                        {inv.po_number || "—"}
                      </td>
                      <td style={{ padding: "14px 16px", fontSize: "12px", color: "#6B5B4A", whiteSpace: "nowrap" }}>
                        {fmtDate(inv.invoice_date)}
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "right", fontSize: "13px", fontWeight: 600, color: "#1A1A1A", whiteSpace: "nowrap" }}>
                        {fmt(inv.total_minor, inv.currency_code)}
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <StatusBadge status={inv.status} />
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <ChevronRight size={16} style={{ color: "#C9A15F" }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "20px" }}>
            {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
              <button key={p} data-testid={`page-btn-${p}`} onClick={() => setPage(p)}
                style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid", cursor: "pointer", fontSize: "13px", background: page === p ? "#1A1A1A" : "transparent", color: page === p ? "#F5EFE6" : "#6B5B4A", borderColor: page === p ? "#1A1A1A" : "#DDD6CE" }}>
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
